import { Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type SourcingEventDto,
} from "../../../../../../autogen/bff-api";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { hasRequestStage, isQualificationScheme, isTwoStageProcedure } from "../../utils/procedure";
import { MilestoneTimeline } from "../MilestoneTimeline";
import { ReadinessBadge } from "./ReadinessBadge";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "BidSubmissionDeadlinePresent",
  "BidSubmissionDeadlineInFuture",
  "BidSubmissionDeadlineAboveMinimum",
  "BidSubmissionDeadlineConfirmed",
  "QuestionDeadlineBetweenAnnouncementAndBidDeadline",
  "RequestDeadlinePresent",
  "RequestDeadlineAboveMinimum",
  "ShortRequestDeadlineJustified",
  "RequestDeadlineConfirmed",
] as const satisfies readonly KnownFieldKey[];

const worseReadiness = (
  ...statuses: (FieldStatusDto | undefined)[]
): FieldStatusDto | undefined =>
  statuses.find((s) => s && !s.satisfied) ?? statuses.find((s) => s !== undefined);

interface Props {
  event: SourcingEventDto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

const formatOsloDateTime = (iso: string): string =>
  `${new Date(iso).toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Oslo",
  })} ${new Date(iso).toLocaleTimeString("nb-NO", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Oslo",
  })}`;

export const DeadlinesAndMilestonesSection = ({ event, readinessFor, onWizardStepNavigate }: Props) => {
  const { t } = useTranslation();
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const deadline = eventData.bidSubmissionDeadline ?? null;
  const validityMonths = publicContext?.eFormsNoticeData?.tenderValidityMonths;
  const tenderValidity = publicContext?.qualificationSchemeCompetition
    ? t("Does not apply to a competition under a qualification system")
    : validityMonths != null
      ? t("{{count}} months", { count: validityMonths })
      : null;
  const twoStage = hasRequestStage(publicContext);

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Deadlines and milestones")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      {twoStage && (
        <Flex gap="2" align="center" mb="4">
          <Text fontSize="sm" color="smText">
            {t("Deadline for requests to participate")}:{" "}
            <b>
              {eventData.requestDeadline
                ? formatOsloDateTime(eventData.requestDeadline)
                : t("Not set")}
            </b>
          </Text>
          <ReadinessBadge
            status={worseReadiness(
              readinessFor("RequestDeadlinePresent"),
              readinessFor("RequestDeadlineAboveMinimum"),
              readinessFor("ShortRequestDeadlineJustified"),
              readinessFor("RequestDeadlineConfirmed"),
            )}
          />
        </Flex>
      )}
      <MilestoneTimeline
        mode="summary"
        event={event}
        deadlineBadge={
          <ReadinessBadge
            status={worseReadiness(
              readinessFor("BidSubmissionDeadlinePresent"),
              readinessFor("BidSubmissionDeadlineInFuture"),
              readinessFor("BidSubmissionDeadlineAboveMinimum"),
              readinessFor("BidSubmissionDeadlineConfirmed"),
              readinessFor("QuestionDeadlineBetweenAnnouncementAndBidDeadline"),
            )}
          />
        }
      />

      {deadline && tenderValidity && (
        <Flex gap="4" mt="5" wrap="wrap">
          <Text fontSize="xs" color="smMuted">
            {t("Tender validity")}: <b>{tenderValidity}</b>
          </Text>
        </Flex>
      )}
    </SummarySection>
  );
};
